import { Box, Grid, Typography, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import { closeModal } from '@store/modals';
import { FlagType, addFlag, orderStore } from '@store/orders';
import { userStore } from '@store/user';
import { LockIcon, NoteIcon } from '@atgfr8/icons';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { Component, createSignal } from 'solid-js';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

import { billingModalClasses as cls } from './classes';

export type BillingHoldModalProps = {
  modalId: string;
  flags?: FlagViewModel[];
  updateFlags?: (flags: Partial<IOrderViewModel>) => void;
};

const BillingHoldModal: Component<BillingHoldModalProps> = (
  props: BillingHoldModalProps,
) => {
  const [comment, setComment] = createSignal('');
  const [loading, setLoading] = createSignal(false);

  const onSetBilling = async (flagType: FlagType, errorMessage: string) => {
    try {
      setLoading(true);
      const payload: Partial<FlagViewModel> = {
        orderId: orderStore.order.id,
        setBy:
          flagType === FlagType.BillingHold ? userStore.user.name : undefined,
        description: comment(),
        flagType,
      };
      const response = await addFlag(
        payload,
        undefined,
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagCreate,
      );
      const partialOrderState: Partial<IOrderViewModel> = {
        flags: [...(props.flags || []), response],
      };

      if (response.flagType === FlagType.NeedsApproval) {
        partialOrderState.needsApprovalFlag = { ...response };
      }
      props.updateFlags && props.updateFlags(partialOrderState);

      handleToast(
        ToastType.Success,
        flagType === FlagType.BillingHold
          ? 'Billing Hold Added'
          : 'Billing Note Added',
      );
    } catch (error) {
      handleToast(ToastType.Error, errorMessage);
    } finally {
      closeModal(props.modalId);
      setLoading(false);
    }
  };

  const onSetBillingHold = () =>
    onSetBilling(FlagType.BillingHold, 'Failed to Add Billing Hold');

  const onSetBillingNote = () =>
    onSetBilling(FlagType.BillingNote, 'Failed to Add Billing Note');

  const onClose = () => {
    closeModal(props.modalId);
  };

  return (
    <Box style={{ 'flex-grow': 1 }}>
      <Grid container spacing={4} class="px-2 pb-4 items-center">
        <Grid item size={11}>
          <Typography class={cls.modalTitle} variant="h6" component="h2">
            Billing Hold
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={4}>
        <Grid item size={12}>
          <TextField
            multiline
            dataTestId={'billing-hold-comment'}
            label="Comment"
            placeholder="Enter description here"
            rows={2}
            value={comment()}
            onChange={(value: string) => {
              setComment(value);
            }}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }} class="border-r my-2">
          <Grid item container spacing={2} class="pr-2">
            <Grid item size={12}>
              <Typography variant="body1" component="p" class={cls.billTitle}>
                Set Billing Hold
              </Typography>
              <Typography variant="body1" component="p" class={cls.billDesc}>
                Billing Holds will not be processed until you release the hold.
                <span class="font-medium">
                  This Order will not be put into the billing queue to be
                  processed until this hold is released by you.
                </span>
              </Typography>
              <Typography
                variant="body1"
                component="p"
                class={cls.billDisclaimer}
              >
                Do not use this to communicate with Billing.
              </Typography>
            </Grid>
            <Grid item size={12} class="flex justify-end">
              <Button
                label="Set Billing Hold"
                variant="contained"
                class={`bg-[#8d001a]! ${
                  loading() || (!comment() && 'bg-[#c47f8c]!')
                } `}
                startIcon={<LockIcon size={20} />}
                onClick={onSetBillingHold}
                isLoading={loading()}
                disabled={!comment() || loading()}
                dataTestId={formatDataTestId('Set Billing Hold', 'button')}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item size={{ xs: 12, md: 6 }} class="my-2">
          <Grid container spacing={2}>
            <Grid item size={12}>
              <Typography variant="body1" component="p" class={cls.billTitle}>
                Set Billing Note
              </Typography>
              <Typography variant="body1" component="p" class={cls.billDesc}>
                If you need someone in Billing to edit or change something prior
                to processing, add a billing note with a message or
                instructions.
              </Typography>
              <Typography
                variant="body1"
                component="p"
                class={cls.billDisclaimer}
              >
                Do not use this to communicate with Billing.
              </Typography>
            </Grid>
            <Grid item size={12} class="flex justify-end">
              <Button
                label="Add Note for Billing"
                variant="contained"
                startIcon={<NoteIcon size={20} />}
                onClick={onSetBillingNote}
                isLoading={loading()}
                disabled={!comment() || loading()}
                dataTestId={formatDataTestId('Add Note for Billing', 'button')}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container class="justify-end">
        <Button
          variant="text"
          label="Cancel"
          disabled={loading()}
          onClick={onClose}
          dataTestId={formatDataTestId('Cancel', 'button')}
        />
      </Grid>
    </Box>
  );
};

export default BillingHoldModal;
